/**
 * Projectile - lightweight actor that travels forward and expires after a distance.
 * Spawns a MeshComponent for visualization. Integrates with physics for collision detection.
 */
import * as THREE from 'three';

import { CharacterStatsComponent } from '../components/CharacterStatsComponent.js';
import { PrimitiveComponent } from '../components/PrimitiveComponent.js';
import { SceneComponent } from '../components/SceneComponent.js';
import { MeshComponent } from '../components/visual/MeshComponent.js';
import { PhysicsVectorParam } from '../physics/PhysicsEngine.js';
import { PhysicsMotionType } from '../physics/PhysicsTypes.js';
import { EngineClass } from '../systems/ClassRegistry.js';
import { DefaultCollisionProfile } from '../systems/collision/CollisionTypes.js';
import { property } from '../utils/serialization/decorator.js';

import { Actor } from './Actor.js';


import type { ActorOptions } from './Actor.js';
import type { CollisionEvent } from '../physics/PhysicsEngine.js';
import type { EditorPropertyChangedResult } from '../utils/EditorClassMeta.js';


export interface ProjectileOptions extends ActorOptions {
  position?: THREE.Vector3;
  direction?: THREE.Vector3;
  velocity?: number;
  range?: number;
  useOverlapCheck?: boolean;
  geometry?: THREE.BufferGeometry;
  material?: THREE.Material;
  damageDealt?: number;
  destroyOnHit?: boolean;
  gravityScale?: number;
  owner?: Actor;
  projectileLifespan?: number;
}

@EngineClass('Projectile')
export class Projectile extends Actor {


  private spawnLocation: THREE.Vector3 | null = null;

  @property()
  private damageDealt: number = 0;

  @property()
  private destroyOnHit: boolean = true;

  @property()
  private gravityScale: number = 0;

  @property()
  private useOverlapCheck: boolean = false;

  public velocity?: number;

  public direction?: THREE.Vector3;

  @property()
  private range?: number;

  // TODO LRK: Correct metadata, incorrect property panel handling.
  @property()
  private geometry?: THREE.BufferGeometry;

  // TODO LRK: Incorrect metadata generation
  @property({ type: 'class', classType: THREE.Material })
  private material?: THREE.Material;

  @property()
  private projectileLifespan?: number;

  private owner?: Actor;

  public override initialize(options?: ProjectileOptions): void {
    super.initialize(options);

    // Store fields
    this.damageDealt = options?.damageDealt ?? this.damageDealt;
    this.destroyOnHit = options?.destroyOnHit ?? this.destroyOnHit;
    this.gravityScale = options?.gravityScale ?? this.gravityScale;
    this.useOverlapCheck = options?.useOverlapCheck ?? this.useOverlapCheck;
    this.range = options?.range ?? this.range;
    this.geometry = options?.geometry ?? this.geometry;
    this.material = options?.material ?? this.material;
    this.projectileLifespan = options?.projectileLifespan ?? this.projectileLifespan;

    // Runtime fields
    this.velocity = options?.velocity ?? this.velocity;
    this.direction = options?.direction ?? this.direction;
    this.owner = options?.owner;

    // Create mesh component after super.initialize()
    this.createMeshComponent();
  }

  private createMeshComponent(): void {
    const mesh = MeshComponent.create({
      name: 'RootComponent',
      physicsOptions: {
        enabled: true,
        enableCCD: true,
        motionType: PhysicsMotionType.Dynamic,
        generateCollisionEvents: true,
        collisionProfile: this.useOverlapCheck ? DefaultCollisionProfile.OverlapAll : DefaultCollisionProfile.BlockAll,
        gravityScale: this.gravityScale
      }
    });
    this.setRootComponent(mesh, true);

    // Apply geometry and material
    if (this.geometry) {
      mesh.geometry = this.geometry;
    } else {
      mesh.geometry = new THREE.SphereGeometry(0.5);
    }

    if (this.material) {
      mesh.material = this.material;
    }
  }

  protected onHit(component: SceneComponent, otherComponent: SceneComponent, event: CollisionEvent): void {
    const otherActor = otherComponent.getActor();

    // Ignore collision with self
    if (otherActor === this) {
      return;
    }

    if (this.destroyOnHit) {
      this.destroy();
    }

    if (this.damageDealt) {
      const statsComponent = otherActor?.getComponent(CharacterStatsComponent);
      statsComponent?.takeDamage(this.damageDealt, {
        hitLocation: event.otherPoint?.clone() ?? new THREE.Vector3(),
        hitNormal: event.otherNormal?.clone() ?? new THREE.Vector3(),
      });
    }
  }

  protected override doBeginPlay(): void {
    super.doBeginPlay();
    const root = this.rootComponent;
    this.spawnLocation = root.position.clone();
    if (root instanceof PrimitiveComponent) {
      root.onCollideWith.add(this.onHit.bind(this));
      root.onOverlapWith.add(this.onHit.bind(this));
      if (this.direction && this.velocity) {
        const linearVelocity = this.direction.clone().multiplyScalar(this.velocity);
        this.getPhysicsEngine()?.setVectorParam(root, PhysicsVectorParam.LinearVelocity, linearVelocity.toArray());
      }

      // Configure collision filtering to ignore owner
      if (this.owner) {
        this.configureOwnerCollisionFiltering(root, this.owner);
      }
    }

    // Set lifespan if configured
    if (this.projectileLifespan) {
      this.setLifeSpan(this.projectileLifespan);
    }
  }

  private configureOwnerCollisionFiltering(primitiveRoot: PrimitiveComponent, owner: Actor): void {
    const physicsEngine = this.getPhysicsEngine();
    if (!physicsEngine) {
      return;
    }

    // Get all physics-enabled components from the owner
    const ownerComponents = owner.getComponents(PrimitiveComponent).filter(comp => comp.isPhysicsEnabled());

    // Configure this projectile to ignore collisions with all owner components
    for (const ownerComponent of ownerComponents) {
      physicsEngine.setComponentCollisionIgnore(primitiveRoot, ownerComponent, true);
    }
  }

  public override tickPostPhysics(deltaTime: number): void {
    super.tickPostPhysics(deltaTime);
    const root = this.rootComponent;
    if (this.spawnLocation && this.range) {
      const distance = root.position.distanceTo(this.spawnLocation);
      if (distance >= this.range) {
        this.destroy();
      }
    }
  }

  public getOwner(): Actor | undefined {
    return this.owner;
  }

  public setOwner(owner: Actor): void {
    this.owner = owner;
    if (this.rootComponent instanceof PrimitiveComponent) {
      this.configureOwnerCollisionFiltering(this.rootComponent, owner);
    }
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override onEditorPropertyChanged(path: string, value: any, result: EditorPropertyChangedResult): void {
    super.onEditorPropertyChanged(path, value, result);

    if (path === 'geometry' || path === 'material') {
      const mesh = this.rootComponent as MeshComponent;
      if (mesh instanceof MeshComponent) {
        if (path === 'geometry' && this.geometry) {
          mesh.geometry = this.geometry;
        }
        if (path === 'material' && this.material) {
          mesh.material = this.material;
        }
      }
    }
  }

  public override getEditorClassIcon(): string | null {
    return 'Icon_Projectile';
  }
}
